import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import type { ManualCheck } from '../../types/manualCheck';
import { bucketChecksByTier } from '../../utils/manualCheckTiers';

interface ManualChecksSalesmanViewProps {
  salesmanUserId: number;
  salesmanName: string;
  startDate?: string;
  endDate?: string;
}

interface TierBucket {
  label: string;
  checks: ManualCheck[];
  totalAmount: number;
  totalCommission: number;
}

/**
 * R16 Task A: per-salesman detail view with per-payout-tier bucketing
 * (20% / 10% / Misc columns) matching Peter's Excel layout.
 *
 * Bucketing happens client-side; the API returns raw check rows for maximum
 * flexibility (a UI toggle could switch layouts without a server change).
 */
export const ManualChecksSalesmanView: React.FC<ManualChecksSalesmanViewProps> = ({
  salesmanUserId,
  salesmanName,
  startDate,
  endDate,
}) => {
  const [checks, setChecks] = useState<ManualCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.getManualChecksForSalesman(salesmanUserId, {
          start_date: startDate,
          end_date: endDate,
        });
        if (!cancelled) {
          setChecks(resp.data?.checks ?? []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [salesmanUserId, startDate, endDate]);

  const buckets: TierBucket[] = React.useMemo(() => {
    // R17 Task B (architect I3): shared tier bucketing via
    // `bucketChecksByTier` (src/utils/manualCheckTiers.ts). Matches the same
    // ±0.05pp tolerance the blade partial enforces so PDF + UI + the new
    // User Report screen section all bucket identically. Extracted for DRY.
    const { tier20, tier10, misc } = bucketChecksByTier(checks);
    const rows: Array<[string, ManualCheck[]]> = [
      ['20%', tier20],
      ['10%', tier10],
      ['Misc', misc],
    ];
    return rows.map(([label, arr]) => ({
      label,
      checks: arr,
      totalAmount: arr.reduce((s, c) => s + Number(c.amount), 0),
      totalCommission: arr.reduce((s, c) => s + Number(c.salesman_commission), 0),
    }));
  }, [checks]);

  const grandTotalAmount = buckets.reduce((s, b) => s + b.totalAmount, 0);
  const grandTotalCommission = buckets.reduce((s, b) => s + b.totalCommission, 0);

  if (loading) return <div className="text-sm text-gray-500">Loading manual checks…</div>;
  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!checks.length) {
    return (
      <div className="text-sm text-gray-500 italic">
        No manual checks for {salesmanName} in this range.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-md font-semibold text-gray-800">
        Manual Checks — {salesmanName}
      </h4>

      {buckets.map((bucket) => (
        bucket.checks.length > 0 && (
          <div key={bucket.label} className="border rounded overflow-hidden">
            <div className="bg-gray-100 px-3 py-2 text-sm font-semibold">
              {bucket.label} bucket
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Firm</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Payout %</th>
                  <th className="px-3 py-2 text-right">Your Commission</th>
                </tr>
              </thead>
              <tbody>
                {bucket.checks.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2">{c.check_date}</td>
                    <td className="px-3 py-2">{c.firm_name}</td>
                    <td className="px-3 py-2 text-right">${Number(c.amount).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{Number(c.payout_percentage).toFixed(2)}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-blue-700">
                      ${Number(c.salesman_commission).toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold border-t">
                  <td colSpan={2} className="px-3 py-2 text-right">
                    {bucket.label} Subtotal:
                  </td>
                  <td className="px-3 py-2 text-right">${bucket.totalAmount.toFixed(2)}</td>
                  <td></td>
                  <td className="px-3 py-2 text-right text-blue-700">
                    ${bucket.totalCommission.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )
      ))}

      <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3 flex justify-between items-center">
        <span className="font-semibold">Grand Total (all buckets)</span>
        <span>
          Amount: <b>${grandTotalAmount.toFixed(2)}</b>
          {' · '}
          Your Commission: <b className="text-blue-700">${grandTotalCommission.toFixed(2)}</b>
        </span>
      </div>
    </div>
  );
};

export default ManualChecksSalesmanView;
